/**
 * The five DOM helpers every screen in `engine/layout` and `engine/landing`
 * is built from.
 *
 * WHY THIS EXISTS AT ALL, given the core auth screens use template literals:
 * every string these screens render comes out of `t()`, and `t()` returns
 * translator-supplied text. Interpolating that into `innerHTML` makes the
 * translation catalogue an injection surface and makes a stray `&` in a
 * company name render as garbage. `el()` only ever sets `textContent`, so a
 * translated string is data in every language, permanently, with no reviewer
 * needing to remember the rule.
 *
 * There is no virtual DOM here and no reactivity. A screen builds a tree, and
 * when something changes it builds a new one — these pages are ~100 nodes, and
 * the whole file is under 2 KB gzipped, which is the point: it sits in the
 * shell budget that a cold visitor pays for before they see anything.
 */

/** Anything `el()` accepts as a child. `null`/`false` are dropped, so a
 *  conditional child is just `cond && el(...)` with no array juggling. */
export type Child = Node | string | number | null | false | undefined;

/**
 * Attribute bag. `class` and `text` are spelled as authors expect;
 * everything else is set with `setAttribute`, so `aria-*`, `data-*`, `role`
 * and friends work without a lookup table. `false`/`null`/`undefined` REMOVE
 * an attribute rather than stringifying, which is what makes
 * `{ hidden: someCondition }` behave.
 */
export interface Attrs {
  readonly class?: string;
  readonly text?: string;
  readonly [name: string]: string | number | boolean | null | undefined;
}

function appendChild(parent: Node, child: Child): void {
  if (child === null || child === undefined || child === false) return;
  if (typeof child === 'string' || typeof child === 'number') {
    parent.appendChild(document.createTextNode(String(child)));
    return;
  }
  parent.appendChild(child);
}

/**
 * Build an element. `el('a', { href: '/faq', text: t('nav.faq') })`.
 *
 * Boolean attributes follow the HTML rule: `true` sets the empty attribute,
 * `false` omits it. That matters for `hidden`, where `hidden="false"` is
 * still hidden.
 */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs?: Attrs,
  children?: readonly Child[],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (attrs !== undefined) {
    for (const [name, value] of Object.entries(attrs)) {
      if (value === null || value === undefined || value === false) continue;
      if (name === 'class') {
        node.className = String(value);
      } else if (name === 'text') {
        node.textContent = String(value);
      } else if (value === true) {
        node.setAttribute(name, '');
      } else {
        node.setAttribute(name, String(value));
      }
    }
  }
  if (children !== undefined) for (const child of children) appendChild(node, child);
  return node;
}

/** Remove every child. Used before a re-render; leaves the host itself alone. */
export function clear(node: Element): void {
  while (node.firstChild !== null) node.removeChild(node.firstChild);
}

/** Replace a host's contents with `children` in one pass. */
export function replaceChildren(host: Element, ...children: readonly Child[]): void {
  clear(host);
  for (const child of children) appendChild(host, child);
}

/**
 * Class list for a link that is also a tap target.
 *
 * 44 CSS px is the WCAG 2.2 target-size floor and Apple's HIG minimum; `h-11`
 * is exactly 44px in Tailwind's default scale. Nav and footer links get this
 * because a footer of 13px text with no padding is unusable on a phone, and
 * that is a structural defect rather than a styling preference.
 */
/**
 * KEEP THE READER WHERE THEY WERE ACROSS A FULL REDRAW.
 *
 * ── THE DEFECT ──────────────────────────────────────────────────────────────
 * The owner, on the shop: "fix that after purchase it scrolls me to the header
 * part of the shop page, let's stay on the same place, maybe user willing to
 * make several purchases".
 *
 * Every screen in this app redraws by rebuilding its whole subtree with
 * `replaceChildren`. For one moment the host is EMPTY, the scroller's content
 * height collapses to nothing, and the browser clamps `scrollTop` to the new
 * maximum — zero. Re-filling it restores the height but not the position, so
 * the reader is at the top. Buying a second thing meant scrolling back down
 * past every shelf, which on a shop with eight shelves is the difference
 * between buying twice and buying once.
 *
 * ── WHY IT TAKES A HELPER AND NOT A LOCAL VARIABLE ──────────────────────────
 * The element that actually scrolls is not the same on every screen or at
 * every width. On desktop the shell's content column carries
 * `md:overflow-y-auto`; below that breakpoint the column is not a scroller at
 * all and the PAGE scrolls instead. Reading `host.scrollTop` gets 0 in both
 * cases, because the host is not the thing that moved. So this walks up to
 * whichever ancestor is genuinely scrollable and falls back to the document
 * scrolling element.
 *
 * ── WHY IT RESTORES SYNCHRONOUSLY ───────────────────────────────────────────
 * In the same task, immediately after the caller's rebuild. A `rAF` or a
 * `setTimeout(0)` would let the browser paint one frame at the top, which is
 * visible as a flash — and worse, some engines then treat that paint as a user
 * scroll. Restoring before the next paint means no frame ever shows the wrong
 * position. The value is clamped by the browser itself if the new content is
 * shorter, which is the correct behaviour: a shelf that vanished cannot be
 * scrolled to.
 */
export function preservingScroll(host: Element, rebuild: () => void): void {
  const doc = host.ownerDocument;
  let scroller: Element | null = null;
  for (let node = host.parentElement; node !== null; node = node.parentElement) {
    const style = doc.defaultView?.getComputedStyle(node);
    const overflowY = style?.overflowY ?? '';
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      scroller = node;
      break;
    }
  }
  // No overflow ancestor means the PAGE is the scroller. `scrollingElement` is
  // the standard way to name it and is correct in both quirks and standards
  // mode, unlike reaching for documentElement or body directly.
  scroller ??= doc.scrollingElement;

  const top = scroller?.scrollTop ?? 0;
  rebuild();
  // Zero is not worth restoring and writing it can itself cancel a smooth
  // scroll the caller started for another reason.
  if (scroller !== null && top > 0) scroller.scrollTop = top;
}

export const TAP_TARGET = 'inline-flex items-center min-h-11 py-2';

/**
 * Visually hidden but read aloud. Tailwind ships `sr-only`; this constant
 * exists so the intent is greppable and a screen never reaches for
 * `display:none`, which hides content from assistive technology too.
 */
export const VISUALLY_HIDDEN = 'sr-only';

/**
 * Bind an event and get its unbinder back, so a screen's teardown is a list of
 * calls rather than a set of matching `removeEventListener` arguments that can
 * drift from the ones passed to `add`.
 */
export function on<K extends keyof HTMLElementEventMap>(
  target: HTMLElement,
  type: K,
  handler: (event: HTMLElementEventMap[K]) => void,
): () => void {
  target.addEventListener(type, handler as EventListener);
  return () => target.removeEventListener(type, handler as EventListener);
}
